import { type ClipType } from '@air/api/types';

/** @public */
export type { ClipType };

/**
 * Events sent from the Air app to the Sanity Studio plugin
 */
const _AIR_TO_SANITY_EVENT = {
  /** User selected an asset */
  AssetSelected: 'air-to-host:asset-selected',
  /** User requested to close the plugin */
  Close: 'air-to-host:close',
  /** Request to open an external URL */
  OpenURL: 'air-to-host:open-url',
  /** Air app has finished loading and is ready */
  Ready: 'air-to-host:ready',
} as const;

/** @public */
export const AirToSanityEvent = _AIR_TO_SANITY_EVENT;

/** @public */
export type AirToSanityEvent = (typeof AirToSanityEvent)[keyof typeof AirToSanityEvent];

/**
 * Custom field from Air
 * @public
 */
export type SelectedAssetCustomField = {
  name: string;
  type: string;
  value: string;
  values: string[];
};

/**
 * Asset URLs structure
 * @public
 */
export type SelectedAssetUrls = {
  /** Thumbnail preview image — always from assets.image regardless of asset type */
  thumbnail: string;
  /** Static URL — raw asset URL (pdf/video/image) at the clip's original dimensions */
  static: string;
  /** Dynamic URL — latest CDN link with followDefaults true (nullable) */
  dynamic: string | null;
  /** Selected URL — depends on version reference: imgix link with chosen dimensions (static) or CDN link (dynamic) */
  selected: string;
};

/**
 * Asset data returned when user selects an asset
 * @public
 */
export type SelectedAssetData = {
  /** Asset ID (groups versions together) */
  id: string;
  /** Version/Clip ID (specific version of the asset) */
  versionId: string;
  /** Asset type (photo, video, audio, etc.) */
  type: ClipType;
  /** Asset URLs */
  urls: SelectedAssetUrls;
  /** Asset filename */
  name: string;
  /** Asset description */
  description?: string;
  /** Asset tags (combined with smart tags) */
  tags?: string[];
  /** Custom fields from Air */
  customFields?: SelectedAssetCustomField[];
  /** Asset width in pixels */
  width?: number;
  /** Asset height in pixels */
  height?: number;
  /** Aspect ratio */
  aspectRatio?: number;
  /** Workspace ID where this asset belongs */
  workspaceId: string;
  /** Version reference mode: 'static' pins to a specific version, 'dynamic' always points to the latest */
  versionReference?: 'static' | 'dynamic';
};

/**
 * Message: Air app is ready
 * @public
 */
export type AirToSanityReady = {
  type: typeof AirToSanityEvent.Ready;
};

/**
 * Message: User selected an asset
 * @public
 */
export type AirToSanityAssetSelected = {
  type: typeof AirToSanityEvent.AssetSelected;
  data: SelectedAssetData;
};

/**
 * Message: User requested to close the plugin
 * @public
 */
export type AirToSanityClose = {
  type: typeof AirToSanityEvent.Close;
};

/**
 * Message: Request to open an external URL
 * @public
 */
export type AirToSanityOpenURL = {
  type: typeof AirToSanityEvent.OpenURL;
  data: { url: string };
};

/**
 * Union type of all messages from Air app to Sanity
 * @public
 */
export type AirToSanityMessage = AirToSanityReady | AirToSanityAssetSelected | AirToSanityClose | AirToSanityOpenURL;
